import { createClient } from '@supabase/supabase-js';

let _supabaseClient = null;

// Get config from environment or storage settings
function getSupabaseConfig() {
  // First try environment variables (for production)
  const envUrl = import.meta.env.VITE_SUPABASE_URL;
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  // Only use env vars if both are present and not empty
  if (envUrl && envKey && envUrl.trim() && envKey.trim()) {
    return { url: envUrl, key: envKey };
  }
  
  // Fall back to storage settings (for user configuration)
  const saved = localStorage.getItem('sno_storage_config');
  if (saved) {
    try {
      const config = JSON.parse(saved);
      if (config.supabaseUrl && config.supabaseKey) {
        return { url: config.supabaseUrl, key: config.supabaseKey };
      }
    } catch (e) {
      console.error('Error parsing storage config:', e);
    }
  }
  
  return null;
}

// Getter that initializes client on first access or when called
export const getSupabaseClient = () => {
  if (!_supabaseClient) {
    const config = getSupabaseConfig();
    if (config) {
      _supabaseClient = createClient(config.url, config.key);
    }
  }
  return _supabaseClient;
};

// Getter property that always returns current client
Object.defineProperty(exports, 'supabase', {
  get: getSupabaseClient,
  enumerable: true
});

// Also export directly for ES modules
export const supabase = new Proxy({}, {
  get: (target, prop) => {
    const client = getSupabaseClient();
    if (!client) {
      throw new Error('Supabase not configured. Please configure your Supabase credentials in Storage Settings.');
    }
    return typeof client[prop] === 'function' ? client[prop].bind(client) : client[prop];
  }
});

// Database operations for projects
export const supabaseApi = {
  projects: {
    // Get all projects for current user
    async getAll() {
      if (!supabase) throw new Error('Supabase not configured');
      
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },

    // Get single project
    async get(id) {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },

    // Create new project
    async create(project) {
      const { data, error } = await supabase
        .from('projects')
        .insert([{
          ...project,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    // Update project
    async update(id, updates) {
      const { data, error } = await supabase
        .from('projects')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    // Delete project
    async delete(id) {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    }
  }
};
